'use client';

import { useState } from 'react';
import styles from './TrustBanner.module.scss';

export interface TrustLogoImage {
  img: string;
  alt: string;
  name?: string;
}

export interface TrustBannerProps {
  label?: string;
  logos: string[] | TrustLogoImage[];
}

function isLogoImage(logo: string | TrustLogoImage): logo is TrustLogoImage {
  return typeof logo === 'object' && logo !== null && 'img' in logo;
}

function LogoItem({ logo, index }: { logo: TrustLogoImage; index: number }) {
  const [hasError, setHasError] = useState(false);
  const displayName = logo.name || logo.alt;

  return (
    <figure className={styles.logoFigure}>
      <div className={styles.logoImageWrapper}>
        {hasError ? (
          <span className={styles.logoFallback}>{displayName}</span>
        ) : (
          <img
            src={logo.img}
            alt={logo.alt}
            className={styles.logoImage}
            loading="lazy"
            onError={() => setHasError(true)}
          />
        )}
      </div>
      {displayName && <figcaption className={styles.logoName}>{displayName}</figcaption>}
    </figure>
  );
}

export function TrustBanner({ label, logos }: TrustBannerProps) {
  if (!logos || logos.length === 0) {
    return null;
  }

  // Duplicate logos to ensure seamless infinite scroll
  const items = [...logos, ...logos];

  return (
    <section className={styles.root}>
      <div className={styles.inner}>
        {label && <p className={styles.label}>{label}</p>}
        <div className={styles.track}>
          {items.map((logo, index) =>
            isLogoImage(logo) ? (
              <LogoItem key={`${logo.img}-${index}`} logo={logo} index={index} />
            ) : (
              <span key={`${logo}-${index}`} className={styles.logo}>
                {logo}
              </span>
            )
          )}
        </div>
      </div>
    </section>
  );
}
